import { useCallback, useId, useMemo, useRef } from 'react';
import { purify } from '@vfit/shared/themes';
import { useAnimation } from '@vfit/shared/hooks';
import { IUseOtpInput } from './otpInput.models';

export const useOtpInput = ({ _error, disabled, numbers, ref }: IUseOtpInput) => {
  const innerRef = useRef<HTMLInputElement | null>(null);
  const focusedRef = useRef<boolean>(false);

  const id = useId();

  const validNumbers = numbers && numbers > 0 ? numbers : 6;

  const cells = useMemo(() => {
    const c = [];

    for (let i = 0; i < validNumbers; i += 1) {
      const uuid = `${id}-${i}`;
      c.push(uuid);
    }

    return c;
  }, [validNumbers]);

  const error = useMemo(() => (_error && !disabled ? _error : ''), [disabled, _error]);

  const errorA = useAnimation({ show: typeof error === 'string' && !!error, thingsToWait: error });

  const errorADelayedThings = useMemo(
    () =>
      purify([
        errorA.delayedThings && typeof errorA.delayedThings === 'string'
          ? errorA.delayedThings
          : '',
      ])[0] || '',
    [errorA.delayedThings]
  );

  const refCallback = useCallback(
    (node: HTMLInputElement) => {
      if (node) {
        innerRef.current = node;

        if (ref) {
          if (typeof ref === 'function') {
            ref(node);
          } else {
            // eslint-disable-next-line no-param-reassign
            ref.current = node;
          }
        }
      }
    },
    [ref]
  );

  const focusFirstCell = useCallback(
    (value?: string) => {
      if (typeof window === 'undefined') return;
      const partialIndex = value?.toString().length ?? 0;
      const finalIndex =
        innerRef.current?.value.length === validNumbers ? validNumbers - 1 : partialIndex;
      const input = document.getElementById(`otp-${cells[finalIndex]}`) as HTMLInputElement;
      if (!input) return;
      input.focus();
    },
    [validNumbers]
  );

  return {
    cells,
    errorA,
    errorADelayedThings,
    error,
    focusedRef,
    id,
    innerRef,
    refCallback,
    focusFirstCell,
  };
};

